import type { VoiceMobileRecordingMime } from '@cloneai/shared-types';

export interface RecordingCheckResult {
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
}

const MIME_CANDIDATES: readonly VoiceMobileRecordingMime[] = [
  'audio/webm',
  'audio/mp4',
  'audio/ogg',
];

export function chooseRecordingMime(): VoiceMobileRecordingMime | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return MIME_CANDIDATES.find((mime) => MediaRecorder.isTypeSupported(mime)) ?? null;
}

export function normalizeRecordingMime(raw: string): VoiceMobileRecordingMime | null {
  const mime = raw.toLowerCase().split(';')[0]?.trim();
  if (!mime) return null;
  return MIME_CANDIDATES.includes(mime as VoiceMobileRecordingMime)
    ? (mime as VoiceMobileRecordingMime)
    : null;
}

export function checkRecording(args: {
  readonly kind: 'consent' | 'sample';
  readonly durationMs: number;
  readonly bytes: number;
  readonly averageRms: number;
  readonly peak: number;
}): RecordingCheckResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const minimum = args.kind === 'sample' ? 5_000 : 1_000;
  if (args.durationMs < minimum) {
    errors.push(
      args.kind === 'sample'
        ? 'Record at least 5 seconds of natural speech.'
        : 'The consent recording is too short. Read the complete phrase.',
    );
  }
  if (args.durationMs > 30_000) errors.push('Recordings must be 30 seconds or shorter.');
  if (args.bytes < 1_000 || args.averageRms < 0.006 || args.peak < 0.012) {
    errors.push('No clear speech was detected. Move closer to the phone and record again.');
  }
  if (args.peak >= 0.98) {
    warnings.push('The audio may be clipping. Move slightly farther from the phone.');
  }
  if (args.averageRms < 0.02 && errors.length === 0) {
    warnings.push(
      'The recording is quiet. A closer, quieter recording may produce a better voice.',
    );
  }
  return { errors, warnings };
}
